import { Vue } from 'vue-property-decorator';
import { popPageManager } from '@/shared/tools/popPageManager';
import { PopPage } from '@/shared/tools/popPageManager/types';
import IdentityBackPopup from './IdentityBackPopup.vue';

/**
 * IdentityBackPopup 组件的适配器类
 * 将 Vue 组件实例包装成符合 PopPage 接口的对象
 */
class IdentityBackPopupAdapter implements PopPage {
  private wrapperInstance: Vue;

  private resolve?: (value: boolean) => void;

  private reject?: () => void;

  public isOpen = false;

  constructor() {
    // 保存回调引用，用于在 render 函数中使用
    const onConfirm = this.handleConfirm.bind(this);
    const onCancel = this.handleCancel.bind(this);
    const onClose = this.handleClose.bind(this);

    // 创建包装器 Vue 实例，用于管理响应式状态
    this.wrapperInstance = new Vue({
      data() {
        return {
          show: false,
        };
      },
      render(h) {
        const vm = this as Vue & { show: boolean };
        return h(IdentityBackPopup, {
          props: {
            show: vm.show,
          },
          on: {
            confirm: onConfirm,
            cancel: onCancel,
            close: onClose,
          },
        });
      },
    });

    // 挂载到 DOM
    const container = document.createElement('div');
    document.body.appendChild(container);
    this.wrapperInstance.$mount(container);
  }

  /**
   * 处理确认事件
   */
  private handleConfirm(): void {
    this.close();
    if (this.resolve) {
      this.resolve(true);
    }
  }

  /**
   * 处理取消事件
   */
  private handleCancel(): void {
    this.close();
    if (this.reject) {
      this.reject();
    }
  }

  /**
   * 处理右上角关闭事件
   */
  private handleClose(): void {
    this.close();
    if (this.resolve) {
      this.resolve(false);
    }
  }

  /**
   * 打开弹窗
   */
  public open(): Promise<boolean> {
    this.isOpen = true;
    // 使用 Vue 的响应式数据来控制显示
    const vm = this.wrapperInstance.$data as { show: boolean };
    vm.show = true;

    return new Promise<boolean>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  /**
   * 关闭弹窗
   */
  public close(): void {
    this.isOpen = false;
    // 使用 Vue 的响应式数据来控制隐藏
    const vm = this.wrapperInstance.$data as { show: boolean };
    vm.show = false;
  }
}

let instance: IdentityBackPopupAdapter;

/**
 * 显示身份验证返回弹窗
 * @param scene 场景标识（可选，保留用于兼容性，当前未使用）
 * @returns Promise<boolean> - 返回 true 表示用户确认，false 表示用户关闭，reject 表示用户取消
 */
export function showIdentityBackPopup(): Promise<boolean> {
  if (!instance) {
    instance = new IdentityBackPopupAdapter();
  }

  popPageManager.addShowedPop(instance);

  return instance.open();
}
